import { Patient } from '@/API';
import { StringConsts } from '@/features/reservation/constants/StringConsts';
import { ReservationUrls } from '@/features/reservation/constants/urls';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

/**
 * 会員の属性に関する処理をまとめたHooks。
 * 属性の種類は以下の通り。なお、会員は属性を複数持つ事も可能。
 *   ・エクソソーム会員
 *   ・ビタミン会員
 * @returns
 */
export const useMemberstatus = () => {
  const { SESSION_STORAGE_KEY_EXOSOME, SESSION_STORAGE_KEY_VITAMIN, STR_TRUE } = StringConsts;
  const navigate = useNavigate();

  /**
   * セッションストレージからエクソソーム会員であるか取得する。
   * @returns エクソソーム会員であるか（true：エクソソーム会員）
   */
  const isExosome = useCallback(() => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY_EXOSOME) === STR_TRUE;
  }, [SESSION_STORAGE_KEY_EXOSOME, STR_TRUE]);

  /**
   * セッションストレージからビタミン会員であるか取得する。
   * @returns ビタミン会員であるか（true：ビタミン会員）
   */
  const isVitamin = useCallback(() => {
    return sessionStorage.getItem(SESSION_STORAGE_KEY_VITAMIN) === STR_TRUE;
  }, [SESSION_STORAGE_KEY_VITAMIN, STR_TRUE]);

  /**
   * セッションストレージから会員のが属性に属しているかを配列で取得する。
   * @returns 会員が属している属性の配列
   */
  const hasMemberstatus = useCallback(() => {
    return [isExosome(), isVitamin()];
  }, [isExosome, isVitamin]);

  /**
   * セッションストレージから会員が属している属性名を配列で取得する。
   * @returns 会員が属している属性の配列
   */
  const getMemberstatusNames = useCallback(() => {
    const names: string[] = [];
    if (hasMemberstatus()[0]) names.push('エクソソーム');
    if (hasMemberstatus()[1]) names.push('ビタミン');

    return names;
  }, [hasMemberstatus]);

  /**
   * 会員登録済みであるかを取得する。
   * @returns 会員登録済みであるか（true：登録済み）
   */
  const isPatient = useCallback((patient: Patient | null | undefined) => {
    return patient !== undefined && patient !== null;
  }, []);

  /**
   * 以下の条件のいずれかに当てはまる場合に画面遷移させる。
   *   ・いずれの属性にも属していない場合
   *   ・会員未登録である場合
   */
  const navigateNotUserRegistration = useCallback(
    (patient: Patient | null | undefined) => {
      if (!hasMemberstatus().find((v) => v) || !isPatient(patient)) navigate(ReservationUrls.NotRegistration);
    },
    [hasMemberstatus, isPatient, navigate]
  );

  return {
    hasMemberstatus,
    getMemberstatusNames,
    isPatient,
    navigateNotUserRegistration
  };
};
